/*  Sports Guard product page — CMS-driven marketing page.
    ────────────────────────────────────────────────────────────────
    iter100.141 — Thin caller over `product-page/sections.jsx`. Every
    hero/section string now lives in `db.cms_content` under page slug
    `product_apex` and is editable from Admin → Landing Page.

    Naming rule:
      • Sub-menu / navigation label stays generic: "Sports Guard".
      • On this PAGE the brand identity "Invisique Apex" is used
        (no ™ per strict user instruction).
*/
import React, { useEffect } from "react";
import { useProductPageCms } from "@/lib/useProductPageCms";
import {
    Hero, TrustBar, PainPoint, Benefits, Engineering, Sports, Journey,
    ComparisonTable, Testimonials, DoctorCredibility, Faq,
    RelatedProducts, FinalCta,
} from "@/components/product-page/sections";
import GuidedCallbackForm from "@/components/site/GuidedCallbackForm";
import SeoHead from "@/components/site/SeoHead";


const BRAND = "Invisique Apex";
const CMS_PAGE = "product_apex";
const TEST_PREFIX = "apex";
const PRODUCT_SLUG = "sports-guard";


const HERO_GALLERY = [
    {
        label: "Product",
        url: "https://customer-assets.emergentagent.com/job_code-fetch-32/artifacts/jloonlfh_eb476738-021f-4cc9-bc00-f4913caf6b34_04e3a219-6153-4f42-a742-ad4150327a72_raw.png",
        alt: "Invisique Apex sports guard on crystal blocks — reinforced, impact-tested",
    },
];

const COMPARISON_HEADERS = [
    "Feature", BRAND, "Store-Bought Guard", "Boil & Bite", "No Guard",
];
const COMPARISON_ROWS = [
    ["Custom Fit", "yes", "no-Generic", "warn-Basic", "na"],
    ["Doctor Supervised", "yes", "no", "no", "no"],
    ["Impact Absorption", "yes-Optimized", "warn-Basic", "no-Minimal", "no"],
    ["Durability", "yes-High", "warn-Medium", "no-Low", "na"],
    ["Comfort During Play", "yes-Excellent", "warn-Bulky", "warn-Uncomfortable", "na"],
    ["Breathability", "yes-High", "warn-Moderate", "no-Poor", "na"],
    ["Speech Impact", "yes-Minimal", "warn-Noticeable", "warn-Noticeable", "no-None"],
    ["Professional Grade", "yes", "no", "no", "na"],
    ["Long-Term Value", "yes-Excellent", "warn-Replacements", "no-Frequent replacement", "no-Expensive repairs"],
];

const RELATED = [
    { title: "Invisique Secure", subtitle: "Sleep protected", body: "Custom night guard for teeth grinding.", href: "/mouthguards-and-retainer/night-guard", icon: "Moon", tint: "from-indigo-100 to-slate-100", iconTint: "text-indigo-600" },
    { title: "Invisique Serene", subtitle: "Quiet nights", body: "For peaceful, uninterrupted sleep.", href: "/mouthguards-and-retainer/anti-snoring", icon: "Bed", tint: "from-sky-100 to-cyan-100", iconTint: "text-sky-600" },
    { title: "Invisique Infinite", subtitle: "Lifetime hold", body: "Keep the smile you worked hard for.", href: "/mouthguards-and-retainer/retainer", icon: "Sparkle", tint: "from-emerald-100 to-teal-100", iconTint: "text-emerald-600" },
];


export default function InvisiqueApexPage() {
    const { t, arr, loading } = useProductPageCms(CMS_PAGE);

    useEffect(() => {
        // SeoHead below drives document.title from CMS.
    }, []);

    const scrollToLeadForm = () => {
        document.getElementById("guided-callback-anchor")
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    if (loading) {
        return <div className="min-h-[60vh] bg-white" aria-hidden="true" data-testid="apex-loading" />;
    }

    return (
        <div className="bg-white" data-testid="invisique-apex-page">
            <SeoHead
                page={CMS_PAGE}
                fallbackTitle={`${BRAND} · Custom Sports Guard · Invisique`}
                fallbackDescription="Custom mouthguard for contact sports. High-impact polymer, doctor-prescribed."
            />
            <Hero
                t={t}
                arr={arr}
                testPrefix={TEST_PREFIX}
                gallery={HERO_GALLERY}
                halosTint="amber"
                captionIcon="ShieldStar"
                credibilityIcon="Trophy"
            />
            <TrustBar t={t} arr={arr} testPrefix={TEST_PREFIX} />
            <PainPoint t={t} arr={arr} testPrefix={TEST_PREFIX} layout="rows" />
            <Benefits t={t} arr={arr} testPrefix={TEST_PREFIX} />
            <Engineering t={t} arr={arr} testPrefix={TEST_PREFIX} />
            <Sports t={t} arr={arr} testPrefix={TEST_PREFIX} />
            <Journey t={t} arr={arr} testPrefix={TEST_PREFIX} bg="bg-white" />
            <ComparisonTable
                t={t}
                arr={arr}
                testPrefix={TEST_PREFIX}
                headers={COMPARISON_HEADERS}
                rows={COMPARISON_ROWS}
            />
            <Testimonials t={t} arr={arr} testPrefix={TEST_PREFIX} bg="bg-white" scope="product_apex" />
            <DoctorCredibility t={t} arr={arr} testPrefix={TEST_PREFIX} gradient="from-primary/[0.06] via-white to-amber-50" />
            <Faq t={t} arr={arr} testPrefix={TEST_PREFIX} />
            <GuidedCallbackForm
                productSlug={PRODUCT_SLUG}
                defaultInterest={PRODUCT_SLUG}
                themeTint="amber"
                testPrefix={TEST_PREFIX}
                headline="Ready to Play Protected? We'll Help You Get Started."
                blurb="Leave your details and an Invisique specialist will reach out to guide you to the nearest certified sports dentist."
                extraSelects={[
                    {
                        key: "sport",
                        label: "Your sport",
                        options: ["Football", "Rugby", "Hockey", "Basketball", "Martial Arts", "Other"],
                    },
                    {
                        key: "competitive_level",
                        label: "Competitive level",
                        options: ["Youth", "High School", "College", "Amateur", "Professional"],
                    },
                ]}
            />
            <RelatedProducts
                t={t}
                arr={arr}
                testPrefix={TEST_PREFIX}
                items={RELATED}
            />
            <FinalCta
                t={t}
                testPrefix={TEST_PREFIX}
                onCallbackClick={scrollToLeadForm}
            />
        </div>
    );
}
